/**
 * Marketplace operations — the SENSIFAI_ADMIN console (audit F-05).
 *
 * Genuinely distinct from the org admin: cross-tenant fleet visibility,
 * catalog stewardship (recipes are data), rollout-phase display, and
 * per-organisation entitlement overrides backed by the Entitlement model.
 * Nothing here is faked: every number comes from /api/v1/ops.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { OpsOrg, OpsOverview, Recipe, RolloutPhase } from '../api/types';
import { Card, Empty, ErrorBox, Loading, StatePill, Tag } from '../components/Metric';
import { ShareBar } from '../components/charts';

const PHASES: RolloutPhase[] = ['PHASE_1', 'PHASE_2', 'PHASE_3', 'PHASE_4'];

function OrgRow({ org, recipes }: { org: OpsOrg; recipes: Recipe[] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [quota, setQuota] = useState<string>('');

  async function setOverride(slug: string, granted: boolean, machineQuota?: number | null) {
    setBusy(true);
    setFail(null);
    setMsg(null);
    try {
      await api.put(`/api/v1/ops/orgs/${org.id}/entitlements`, {
        recipeSlug: slug,
        granted,
        ...(machineQuota !== undefined ? { machineQuota } : {}),
      });
      setMsg(`Override saved for ${slug}.`);
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function removeOverride(slug: string) {
    setBusy(true);
    setFail(null);
    setMsg(null);
    try {
      await api.del(`/api/v1/ops/orgs/${org.id}/entitlements${qs({ recipeSlug: slug })}`);
      setMsg(`Override removed for ${slug} — plan defaults apply again.`);
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const overrideSlugs = new Set(org.plan.overrides.map((o) => o.recipeSlug));

  return (
    <>
      <tr>
        <td style={{ fontWeight: 650 }}>{org.name}</td>
        <td className="mono dim">{org.slug}</td>
        <td className="num">{org.users}</td>
        <td className="num">{org.machines}</td>
        <td className="num">{org.stations}</td>
        <td>
          <StatePill state={org.accessDecision} />
          <div className="dim">plan {org.plan.key}</div>
        </td>
        <td style={{ textAlign: 'right' }}>
          <button className="btn sm" onClick={() => setOpen(!open)}>
            {open ? 'Close' : 'Entitlements'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} style={{ background: 'var(--surface-2)' }}>
            <div style={{ padding: '10px 6px' }}>
              <div className="btn-row" style={{ marginBottom: 10 }}>
                <Tag kind="brand">plan: {org.plan.label}</Tag>
                <Tag>
                  quota:{' '}
                  {org.plan.machineQuota != null
                    ? `${org.licensedMachines}/${org.plan.machineQuota} machines`
                    : `${org.licensedMachines} machines (no quota)`}
                </Tag>
                <Tag kind="warn">overrides win over the package</Tag>
              </div>
              {msg && <div className="callout info" style={{ marginBottom: 8 }}>{msg}</div>}
              {fail && <div className="error-box" style={{ marginBottom: 8 }}>{fail}</div>}
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Recipe</th>
                      <th>Phase</th>
                      <th>Effective</th>
                      <th>Source</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {recipes.map((r) => {
                      const ov = org.plan.overrides.find((o) => o.recipeSlug === r.slug);
                      const effective = org.plan.recipeSlugs.includes(r.slug);
                      return (
                        <tr key={r.id}>
                          <td style={{ fontWeight: 600 }}>{r.name}</td>
                          <td className="mono dim">{r.rolloutPhase}</td>
                          <td>
                            {effective ? <span className="pill ok">included</span> : <span className="pill muted">not included</span>}
                          </td>
                          <td className="dim">
                            {ov ? (ov.granted ? 'override: granted' : 'override: revoked') : 'plan package'}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
                              {ov ? (
                                <button className="btn sm ghost" disabled={busy} onClick={() => void removeOverride(r.slug)}>
                                  Remove override
                                </button>
                              ) : (
                                <>
                                  <button className="btn sm" disabled={busy} onClick={() => void setOverride(r.slug, true)}>
                                    Grant
                                  </button>
                                  <button className="btn sm danger" disabled={busy} onClick={() => void setOverride(r.slug, false)}>
                                    Revoke
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="btn-row" style={{ marginTop: 10 }}>
                <label htmlFor={`quota-${org.id}`} style={{ fontSize: 12, color: 'var(--ink-2)', fontWeight: 600 }}>
                  Machine-quota override
                </label>
                <input
                  id={`quota-${org.id}`}
                  style={{ maxWidth: 110 }}
                  inputMode="numeric"
                  placeholder="e.g. 5"
                  value={quota}
                  onChange={(e) => setQuota(e.target.value)}
                />
                <button
                  className="btn sm"
                  disabled={busy || !quota}
                  onClick={() => {
                    const first = recipes[0];
                    if (first) void setOverride(first.slug, org.plan.recipeSlugs.includes(first.slug), Number(quota));
                  }}
                >
                  Save quota
                </button>
                <span className="dim" style={{ fontSize: 11.5 }}>
                  Stored on an entitlement row; applies org-wide. {overrideSlugs.size} override row(s) exist.
                </span>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function OpsPage() {
  const overview = useApi<OpsOverview>(() => api.get<OpsOverview>('/api/v1/ops/overview'), []);
  const orgs = useApi<OpsOrg[]>(() => api.get<OpsOrg[]>('/api/v1/ops/orgs'), []);
  const recipes = useApi<Recipe[]>(() => api.get<Recipe[]>('/api/v1/recipes'), []);

  const [busyRecipe, setBusyRecipe] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [fail, setFail] = useState<string | null>(null);

  async function patchRecipe(id: string, patch: Record<string, unknown>) {
    setBusyRecipe(id);
    setFail(null);
    setMsg(null);
    try {
      await api.patch(`/api/v1/ops/recipes/${id}`, patch);
      setMsg('Catalog updated. Changes apply to new reads immediately.');
      overview.reload();
    } catch (err) {
      setFail((err as Error).message);
    } finally {
      setBusyRecipe(null);
    }
  }

  if (overview.loading || orgs.loading) return <Loading label="Loading platform…" />;
  if (overview.error) return <ErrorBox message={overview.error} />;
  if (orgs.error) return <ErrorBox message={orgs.error} />;
  const o = overview.data!;

  return (
    <>
      <div className="callout info">
        <strong>Marketplace operations — cross-tenant</strong>
        This console sees every organisation. Edits are audited with your user
        id. Catalog changes are data edits (spec 2.1); rollout-phase changes
        additionally require the catalog:rollout:manage capability.
      </div>

      {msg && <div className="callout info">{msg}</div>}
      {fail && <div className="error-box">{fail}</div>}

      <div className="grid grid-4">
        <Card className="metric">
          <div className="metric-label">Organisations</div>
          <div className="metric-value">{o.organisations}</div>
          <div className="metric-sub">{o.users} users</div>
        </Card>
        <Card className="metric">
          <div className="metric-label">Fleet</div>
          <div className="metric-value">{o.machines}</div>
          <div className="metric-sub">
            {o.sites} sites · {o.stationsTotal} stations
          </div>
        </Card>
        <Card className="metric">
          <div className="metric-label">Inspection events</div>
          <div className="metric-value">{o.inspectionEvents}</div>
          <div className="metric-sub">{o.liveSubscribers} live subscribers</div>
        </Card>
        <Card className="metric">
          <div className="metric-label">Rollout ceiling</div>
          <div className="metric-value" style={{ fontSize: 20 }}>{o.rolloutCeiling}</div>
          <div className="metric-sub">deployment env — change requires redeploy</div>
        </Card>
      </div>

      <div className="grid grid-2" style={{ marginTop: 14 }}>
        <Card title="Stations by state (all tenants)">
          <ShareBar
            parts={[
              { label: 'Active', value: o.stationsByState.ACTIVE ?? 0, color: 'var(--ok)' },
              { label: 'Shadow', value: o.stationsByState.SHADOW ?? 0, color: 'var(--brand)' },
              { label: 'Passive', value: o.stationsByState.PASSIVE ?? 0, color: 'var(--border-strong)' },
            ]}
          />
        </Card>

        <Card
          title="Catalog stewardship"
          note="Recipes are data. Availability and copy edit instantly; the phase column shows where each recipe ships. Changing a phase is a rollout decision, not a copy edit."
        >
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Recipe</th>
                  <th>Phase</th>
                  <th className="num">Bound</th>
                  <th>Available</th>
                </tr>
              </thead>
              <tbody>
                {o.recipes.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>
                      <select
                        aria-label={`Rollout phase for ${r.name}`}
                        style={{ width: 120, padding: '4px 6px', fontSize: 12 }}
                        value={r.rolloutPhase}
                        disabled={busyRecipe === r.id}
                        onChange={(e) => void patchRecipe(r.id, { rolloutPhase: e.target.value })}
                      >
                        {PHASES.map((p) => (
                          <option key={p}>{p}</option>
                        ))}
                      </select>
                    </td>
                    <td className="num">{r.boundStations}</td>
                    <td>
                      <button
                        className={`btn sm ${r.available ? '' : 'primary'}`}
                        disabled={busyRecipe === r.id}
                        onClick={() => void patchRecipe(r.id, { available: !r.available })}
                      >
                        {r.available ? 'Mark not shipped' : 'Mark shipped'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 14 }}>
        <Card
          title="Organisations"
          note="Cross-tenant fleet and subscription visibility. Open a row to manage entitlement overrides."
        >
          {!orgs.data || orgs.data.length === 0 ? (
            <Empty title="No organisations yet" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Organisation</th>
                    <th>Slug</th>
                    <th className="num">Users</th>
                    <th className="num">Machines</th>
                    <th className="num">Stations</th>
                    <th>Access</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {orgs.data.map((org) => (
                    <OrgRow key={org.id} org={org} recipes={recipes.data ?? []} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
